import { Component, Project } from "projen";
import { TypeScriptProject } from "../projects/typescript-project";
import { TurboRepo } from "../turbo/turbo-repo";
import { TurboRepoTask } from "../turbo/turbo-repo-task";

/*******************************************************************************
 *
 * Reset Task Component
 *
 * Adds a "reset" task that deletes all build artifacts produced by the build
 * process. This includes node_modules, lib, dist, coverage, test-reports,
 * .turbo, and tsconfig.tsbuildinfo.
 *
 ******************************************************************************/

export interface ResetTaskOptions {
  /**
   * Custom output directory to delete (overrides tsconfig artifactsDirectory detection).
   *
   * @default - detected from typescript project.artifactsDirectory or "lib"
   */
  readonly artifactsDirectory?: string;

  /**
   * Array of glob patterns for paths to remove.
   * If empty, the artifactsDirectory will be added automatically.
   *
   * @default []
   */
  readonly pathsToRemove?: string[];

  /**
   * Name of the task to create.
   *
   * @default "reset"
   */
  readonly taskName?: string;
}

export class ResetTask extends Component {
  /**
   * Static method to discover reset task in a project.
   */
  public static of(project: Project): ResetTask | undefined {
    const isDefined = (c: Component): c is ResetTask => c instanceof ResetTask;
    return project.components.find(isDefined);
  }

  /**
   * The output directory to delete (from tsconfig or custom).
   */
  public readonly artifactsDirectory: string;

  /**
   * The final array of paths that will be removed by the reset task.
   */
  public readonly pathsToRemove: string[];

  /**
   * The name of the task that was created.
   */
  public readonly taskName: string;

  constructor(
    public readonly project: Project,
    options: ResetTaskOptions = {},
  ) {
    super(project);

    /**
     * Determine the output directory.
     * 1. Use custom artifactsDirectory if provided
     * 2. Use the artifacts directory if project is TypeScriptProject
     * 3. Default to "lib"
     */
    this.artifactsDirectory = options.artifactsDirectory
      ? options.artifactsDirectory
      : project instanceof TypeScriptProject
        ? project.artifactsDirectory
        : "lib";

    /**
     * Build the paths array to remove.
     * If pathsToRemove is empty, add artifactsDirectory.
     * If pathsToRemove is not empty, use it as-is (artifactsDirectory is not automatically added).
     * Remove duplicate entries from the array.
     */
    const pathsToRemove = options.pathsToRemove ?? [];
    const finalPaths =
      pathsToRemove.length === 0 ? [this.artifactsDirectory] : pathsToRemove;

    /**
     * Remove duplicate paths from the array.
     */
    this.pathsToRemove = Array.from(new Set(finalPaths));

    /**
     * Determine the task name.
     */
    this.taskName = options.taskName ?? "reset";

    /**
     * Create the reset task.
     */
    const resetTask = this.project.tasks.addTask(this.taskName, {
      description:
        "Delete build artifacts specified by pathsToRemove option, or artifactsDirectory if pathsToRemove is empty",
    });

    /**
     * Delete build artifacts with existence checks.
     * Using shell conditionals to only delete if paths exist.
     * Using -e to check existence (works for both files and directories).
     */
    this.pathsToRemove.forEach((path) => {
      resetTask.exec(`[ -e "${path}" ] && rm -rf ${path} || true`);
    });

    /**
     * Turbo tasks are added after all components are created.
     * This assumes turbo is built first so that it's available to be
     * referenced. This could introduce problems in future but works for now.
     */

    /**
     * Check if turborepo is active.
     */
    const rootHasTurbo = TurboRepo.of(this.project.root) !== undefined;
    const isSubproject = this.project !== this.project.root;
    const isRootProject = this.project === this.project.root;

    /**
     * If turborepo is active for the subproject, add turbo tasks.
     */
    if (isSubproject && rootHasTurbo) {
      /**
       * Get TurboRepo instance for this subproject.
       * It should exist by now since preSynthesize runs after all components are created.
       */
      const turbo = TurboRepo.of(this.project);
      if (turbo && !turbo.isRootProject) {
        /**
         * Create turbo:reset task (or `turbo:${taskName}`).
         */
        const turboResetTask = new TurboRepoTask(this.project, {
          name: `turbo:${this.taskName}`,
          cache: false,
        });
        turbo.tasks.push(turboResetTask);

        /**
         * Create a turbo task with the same name as the reset task.
         */
        const turboTaskWithSameName = new TurboRepoTask(this.project, {
          name: this.taskName,
          cache: false,
        });
        turbo.tasks.push(turboTaskWithSameName);

        /**
         * turboResetTask depends on turboTaskWithSameName so that the reset task
         * can be run across all suprojects using turbo:reset as the entry point.
         */
        turboResetTask.dependsOn.push(turboTaskWithSameName.name);
      }
    }

    /**
     * If reset-task is in the root project, add a turbo task that will trigger
     * all the subtasks using dependsOn of "^turbo:reset".
     */
    if (isRootProject && rootHasTurbo) {
      const turbo = TurboRepo.of(this.project);
      if (turbo && turbo.isRootProject) {
        /**
         * Create turbo:reset task (or `turbo:${taskName}`) in the root project.
         * The `^` prefix means "run this task in all dependencies first".
         */
        const rootTurboResetTask = new TurboRepoTask(this.project, {
          name: `turbo:${this.taskName}`,
          dependsOn: [`^turbo:${this.taskName}`],
          cache: false,
        });
        turbo.tasks.push(rootTurboResetTask);

        /**
         * Create a turbo task with the same name as the reset task.
         */
        const turboTaskWithSameName = new TurboRepoTask(this.project, {
          name: this.taskName,
          cache: false,
        });
        turbo.tasks.push(turboTaskWithSameName);

        /**
         * rootTurboResetTask depends on turboTaskWithSameName so that the reset task
         * can be run across all suprojects using turbo:reset as the entry point.
         */
        rootTurboResetTask.dependsOn.push(turboTaskWithSameName.name);

        /**
         * Create reset:all task - a projen task that calls the turbo task.
         * This is similar to build:all in turbo-repo.ts.
         */
        const resetAllTask = this.project.tasks.addTask(
          `${this.taskName}:all`,
          {
            description: `Reset all build artifacts across the monorepo by running turbo:${this.taskName} in all subprojects.`,
          },
        );
        resetAllTask.exec("turbo telemetry disable");
        resetAllTask.exec(
          `turbo turbo:${this.taskName} ${turbo.renderRunArgs(false)}`,
        );
        resetAllTask.exec(`pnpm ${this.taskName}`);
      }
    }
  }
}
